import { useMemo, useState, useRef, useCallback } from "react";
import { RotateCcw, Move } from "lucide-react";

const WOOD_STAINS_HEX = {
  // PINE RANGE - lighter base, shows grain well
  antique_stripped_pine: "#E8D4A8",
  golden_pine: "#D4A84B",
  dark_pine: "#8B6914",
  // MAHOGANY RANGE - rich reds
  brown_mahogany: "#5C3317",
  very_red_mahogany: "#8B2500",
  dark_red_mahogany: "#5C1A0B",
  // OAK RANGE - golden to brown
  light_oak: "#C9A86C",
  golden_oak: "#B8860B",
  medium_oak: "#A0785A",
  french_oak: "#8B7355",
  english_oak: "#6B4423",
  // WALNUT & ROSEWOOD
  dark_walnut: "#4A3728",
  light_rosewood: "#9C6B5C",
  light_sycamore: "#F5DEB3",
  // BLACK RANGE
  black_black: "#1A1A1A",
  black_ebony: "#2D2D2D",
  // GREY RANGE
  ironstone_grey: "#4A4A4A",
  driftwood_grey: "#78736E",
  moonlight_grey: "#B0B0B0",
  dovetail_grey: "#6E6E6E",
  // WHITE & SPECIAL
  soft_white: "#F5F5F0",
  police_box_blue: "#003B6F",
  christmas_green: "#165B33",
  christmas_red: "#B31B1B",
  // NATURAL
  natural: "#E3C28D",
};

// Utility functions for color manipulation
const darkerShade = (hex, amount) => {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
  const b = Math.max(0, (num & 0x0000FF) - amount);
  return `rgb(${r},${g},${b})`;
};

const lighterShade = (hex, amount) => {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0x00FF) + amount);
  const b = Math.min(255, (num & 0x0000FF) + amount);
  return `rgb(${r},${g},${b})`;
};

// Realistic wood grain pattern definitions for SVG
const WoodGrainDefs = ({ stainHex }) => {
  return (
    <>
      {/* Base wood grain texture - horizontal grain lines */}
      <pattern id="woodGrainBase" patternUnits="userSpaceOnUse" width="100" height="20" patternTransform="rotate(2)">
        <rect width="100" height="20" fill={stainHex} />
        {/* Primary grain lines */}
        <path d="M0,3 Q25,1 50,4 T100,3" stroke={darkerShade(stainHex, 25)} strokeWidth="1.2" fill="none" opacity="0.5" />
        <path d="M0,7 Q30,5 60,8 T100,6" stroke={darkerShade(stainHex, 35)} strokeWidth="0.8" fill="none" opacity="0.45" />
        <path d="M0,11 Q20,9 45,12 T100,10" stroke={darkerShade(stainHex, 20)} strokeWidth="1" fill="none" opacity="0.4" />
        <path d="M0,15 Q35,13 70,16 T100,14" stroke={darkerShade(stainHex, 30)} strokeWidth="0.6" fill="none" opacity="0.35" />
        <path d="M0,18 Q15,17 40,19 T100,18" stroke={darkerShade(stainHex, 15)} strokeWidth="0.5" fill="none" opacity="0.3" />
        {/* Fine grain detail */}
        <path d="M0,2 Q40,0.5 80,2.5 T100,1.5" stroke={darkerShade(stainHex, 18)} strokeWidth="0.3" fill="none" opacity="0.25" />
        <path d="M0,9 Q50,7 90,10 T100,8" stroke={darkerShade(stainHex, 22)} strokeWidth="0.4" fill="none" opacity="0.28" />
        <path d="M0,13 Q25,12 55,14 T100,12.5" stroke={darkerShade(stainHex, 28)} strokeWidth="0.35" fill="none" opacity="0.22" />
      </pattern>
      
      {/* Wood texture with knots - used for larger surfaces */}
      <pattern id="woodGrainWithKnots" patternUnits="userSpaceOnUse" width="200" height="120">
        <rect width="200" height="120" fill="url(#woodGrainBase)" />
        {/* Natural wood knot */}
        <ellipse cx="60" cy="35" rx="6" ry="4" fill={darkerShade(stainHex, 45)} opacity="0.4" />
        <ellipse cx="60" cy="35" rx="4" ry="2.5" fill={darkerShade(stainHex, 55)} opacity="0.35" />
        <ellipse cx="60" cy="35" rx="2" ry="1" fill={darkerShade(stainHex, 65)} opacity="0.3" />
        {/* Grain curves around knot */}
        <path d="M45,30 Q60,25 75,30" stroke={darkerShade(stainHex, 30)} strokeWidth="0.5" fill="none" opacity="0.3" />
        <path d="M45,40 Q60,45 75,40" stroke={darkerShade(stainHex, 30)} strokeWidth="0.5" fill="none" opacity="0.3" />
        {/* Second smaller knot */}
        <ellipse cx="150" cy="85" rx="4" ry="3" fill={darkerShade(stainHex, 40)} opacity="0.3" />
        <ellipse cx="150" cy="85" rx="2" ry="1.5" fill={darkerShade(stainHex, 50)} opacity="0.25" />
      </pattern>
      
      {/* Vertical grain for side panels */}
      <pattern id="woodGrainVertical" patternUnits="userSpaceOnUse" width="20" height="100" patternTransform="rotate(88)">
        <rect width="20" height="100" fill={stainHex} />
        <path d="M3,0 Q1,25 4,50 T3,100" stroke={darkerShade(stainHex, 25)} strokeWidth="1" fill="none" opacity="0.45" />
        <path d="M7,0 Q5,30 8,60 T6,100" stroke={darkerShade(stainHex, 32)} strokeWidth="0.7" fill="none" opacity="0.4" />
        <path d="M11,0 Q9,20 12,45 T10,100" stroke={darkerShade(stainHex, 20)} strokeWidth="0.9" fill="none" opacity="0.38" />
        <path d="M15,0 Q13,35 16,70 T14,100" stroke={darkerShade(stainHex, 28)} strokeWidth="0.5" fill="none" opacity="0.32" />
        <path d="M18,0 Q17,25 19,55 T17,100" stroke={darkerShade(stainHex, 15)} strokeWidth="0.4" fill="none" opacity="0.28" />
      </pattern>
      
      {/* Sheen/lighting effect overlay */}
      <linearGradient id="woodSheen" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={lighterShade(stainHex, 25)} stopOpacity="0.15" />
        <stop offset="30%" stopColor={stainHex} stopOpacity="0" />
        <stop offset="70%" stopColor={stainHex} stopOpacity="0" />
        <stop offset="100%" stopColor={darkerShade(stainHex, 20)} stopOpacity="0.12" />
      </linearGradient>
      
      {/* Panel shadows for depth */}
      <linearGradient id="panelShadowLeft" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#000" stopOpacity="0.2" />
        <stop offset="100%" stopColor="#000" stopOpacity="0" />
      </linearGradient>
      
      <linearGradient id="panelShadowTop" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#000" stopOpacity="0.15" />
        <stop offset="100%" stopColor="#000" stopOpacity="0" />
      </linearGradient>
      
      {/* Highlight for edges */}
      <linearGradient id="edgeHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fff" stopOpacity="0.1" />
        <stop offset="50%" stopColor="#fff" stopOpacity="0.05" />
        <stop offset="100%" stopColor="#fff" stopOpacity="0" />
      </linearGradient>
    </>
  );
};

const EnclosureViewer = ({ config }) => {
  const { shape, width, height, depth, stain_color } = config;
  const stainHex = WOOD_STAINS_HEX[stain_color] || WOOD_STAINS_HEX.natural;
  
  // Rotation state (in degrees)
  const [rotationY, setRotationY] = useState(45); // Horizontal rotation
  const [rotationX, setRotationX] = useState(25); // Vertical tilt
  const [isDragging, setIsDragging] = useState(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  // Calculate SVG viewBox and dimensions
  const svgWidth = 400;
  const svgHeight = 300;
  const padding = 40;
  
  // Scale factor for the enclosure
  const maxDimension = Math.max(width, height, depth);
  const scale = (svgWidth - padding * 2) / (maxDimension * 1.8);
  
  // Convert rotation to radians
  const rotYRad = (rotationY * Math.PI) / 180;
  const rotXRad = (rotationX * Math.PI) / 180;
  
  // 3D rotation and projection
  const project3D = useCallback((x, y, z) => {
    // Rotate around Y axis (horizontal rotation)
    const cosY = Math.cos(rotYRad);
    const sinY = Math.sin(rotYRad);
    const x1 = x * cosY - z * sinY;
    const z1 = x * sinY + z * cosY;
    
    // Rotate around X axis (vertical tilt)
    const cosX = Math.cos(rotXRad);
    const sinX = Math.sin(rotXRad);
    const y1 = y * cosX - z1 * sinX;
    const z2 = y * sinX + z1 * cosX;
    
    // Isometric projection with perspective
    const perspective = 800;
    const projectionScale = perspective / (perspective + z2);
    
    return {
      x: svgWidth / 2 + x1 * scale * projectionScale,
      y: svgHeight / 2 - y1 * scale * projectionScale,
      z: z2 // Keep z for depth sorting
    };
  }, [rotYRad, rotXRad, scale, svgWidth, svgHeight]);
  
  // Mouse/touch handlers for rotation
  const handleMouseDown = (e) => {
    setIsDragging(true);
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;
    
    setRotationY(prev => (prev + deltaX * 0.5) % 360);
    setRotationX(prev => Math.max(-60, Math.min(60, prev - deltaY * 0.3)));
    
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  }, [isDragging]);
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    
    const deltaX = e.touches[0].clientX - lastMousePos.current.x;
    const deltaY = e.touches[0].clientY - lastMousePos.current.y;
    
    setRotationY(prev => (prev + deltaX * 0.5) % 360);
    setRotationX(prev => Math.max(-60, Math.min(60, prev - deltaY * 0.3)));
    
    lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, [isDragging]);
  
  const handleTouchEnd = () => {
    setIsDragging(false);
  };
  
  const resetRotation = () => {
    setRotationY(45);
    setRotationX(25);
  };

  // Helper to create polygon points string
  const pointsString = (vertices) => {
    return vertices.map(v => `${v.x},${v.y}`).join(' ');
  };
  
  // Calculate face visibility and average Z for sorting
  const getFaceData = (vertices) => {
    const avgZ = vertices.reduce((sum, v) => sum + v.z, 0) / vertices.length;
    // Calculate normal for backface culling (simplified)
    const v1 = { x: vertices[1].x - vertices[0].x, y: vertices[1].y - vertices[0].y };
    const v2 = { x: vertices[2].x - vertices[0].x, y: vertices[2].y - vertices[0].y };
    const cross = v1.x * v2.y - v1.y * v2.x;
    return { avgZ, visible: cross > 0 };
  };

  const useHingedDoors = shape === "corner" || shape === "hexagonal" || (width > 5 && height > 5);

  // Plinth dimensions (as fractions of total height). Top plinth <=5%, bottom plinth <=10%.
  const topPlinthH = height * 0.05;
  const bottomPlinthH = height * 0.10;

  const renderRectangular = useMemo(() => {
    const w = width / 2;
    const h = height / 2;
    const d = depth / 2;
    
    // Define the 8 corners
    const corners = {
      bbl: project3D(-w, -h, -d),
      bbr: project3D(w, -h, -d),
      btl: project3D(-w, h, -d),
      btr: project3D(w, h, -d),
      fbl: project3D(-w, -h, d),
      fbr: project3D(w, -h, d),
      ftl: project3D(-w, h, d),
      ftr: project3D(w, h, d),
      // Plinth boundary points on the front face
      bptl: project3D(-w, -h + bottomPlinthH, d),  // bottom plinth top-left
      bptr: project3D(w, -h + bottomPlinthH, d),   // bottom plinth top-right
      tpbl: project3D(-w, h - topPlinthH, d),      // top plinth bottom-left
      tpbr: project3D(w, h - topPlinthH, d),       // top plinth bottom-right
    };

    // Door frame geometry (for hinged doors)
    const frameThick = 0.25;
    const beamHalf = 0.1;
    const doorFrame = useHingedDoors ? {
      // Outer frame corners (same as front face)
      obl: corners.fbl, obr: corners.fbr, otl: corners.ftl, otr: corners.ftr,
      // Inner frame corners
      ibl: project3D(-w + frameThick, -h + frameThick, d),
      ibr: project3D(w - frameThick, -h + frameThick, d),
      itl: project3D(-w + frameThick, h - frameThick, d),
      itr: project3D(w - frameThick, h - frameThick, d),
      // Centre beam
      cbl: project3D(-beamHalf, -h, d),
      cbr: project3D(beamHalf, -h, d),
      ctl: project3D(-beamHalf, h, d),
      ctr: project3D(beamHalf, h, d),
      // Left glass panel inner
      lgl: project3D(-w + frameThick, -h + frameThick, d),
      lgr: project3D(-beamHalf, -h + frameThick, d),
      lgtl: project3D(-w + frameThick, h - frameThick, d),
      lgtr: project3D(-beamHalf, h - frameThick, d),
      // Right glass panel inner
      rgl: project3D(beamHalf, -h + frameThick, d),
      rgr: project3D(w - frameThick, -h + frameThick, d),
      rgtl: project3D(beamHalf, h - frameThick, d),
      rgtr: project3D(w - frameThick, h - frameThick, d),
    } : null;
    
    // Define faces with their vertices
    const faces = [
      { name: 'back', verts: [corners.bbl, corners.bbr, corners.btr, corners.btl], isGlass: false, pattern: 'woodGrainWithKnots' },
      { name: 'front', verts: [corners.fbr, corners.fbl, corners.ftl, corners.ftr], isGlass: !useHingedDoors, isDoorFront: useHingedDoors },
      { name: 'left', verts: [corners.fbl, corners.bbl, corners.btl, corners.ftl], isGlass: false, pattern: 'woodGrainVertical' },
      { name: 'right', verts: [corners.bbr, corners.fbr, corners.ftr, corners.btr], isGlass: false, pattern: 'woodGrainVertical' },
      { name: 'top', verts: [corners.btl, corners.btr, corners.ftr, corners.ftl], isGlass: false, pattern: 'woodGrainBase' },
      { name: 'bottom', verts: [corners.fbl, corners.fbr, corners.bbr, corners.bbl], isGlass: false, pattern: 'woodGrainWithKnots' },
    ];
    
    // Calculate visibility and sort by depth
    const visibleFaces = faces.map(face => {
      const { avgZ, visible } = getFaceData(face.verts);
      return { ...face, avgZ, visible };
    }).filter(f => f.visible).sort((a, b) => b.avgZ - a.avgZ);
    
    return (
      <g>
        {visibleFaces.map((face, i) => (
          <g key={face.name}>
            {face.isDoorFront && doorFrame ? (
              <>
                {/* Wood frame border around the front */}
                <polygon
                  points={pointsString(face.verts)}
                  fill={`url(#woodGrainBase)`}
                  stroke={darkerShade(stainHex, 15)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
                {/* Left glass door */}
                <polygon
                  points={pointsString([doorFrame.lgl, doorFrame.lgr, doorFrame.lgtr, doorFrame.lgtl])}
                  fill="#88ccff"
                  stroke="#88ccff"
                  strokeWidth="1"
                  strokeLinejoin="round"
                  opacity="0.3"
                />
                <polygon
                  points={pointsString([doorFrame.lgl, doorFrame.lgr, doorFrame.lgtr, doorFrame.lgtl])}
                  fill="url(#glassGradient)"
                  opacity="0.2"
                />
                {/* Right glass door */}
                <polygon
                  points={pointsString([doorFrame.rgl, doorFrame.rgr, doorFrame.rgtr, doorFrame.rgtl])}
                  fill="#88ccff"
                  stroke="#88ccff"
                  strokeWidth="1"
                  strokeLinejoin="round"
                  opacity="0.3"
                />
                <polygon
                  points={pointsString([doorFrame.rgl, doorFrame.rgr, doorFrame.rgtr, doorFrame.rgtl])}
                  fill="url(#glassGradient)"
                  opacity="0.2"
                />
                {/* Centre support beam */}
                <polygon
                  points={pointsString([doorFrame.cbl, doorFrame.cbr, doorFrame.ctr, doorFrame.ctl])}
                  fill={`url(#woodGrainVertical)`}
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                {/* Hinges - left door (left side) */}
                {[0.25, 0.5, 0.75].map(t => {
                  const y = -h + height * t;
                  const hinge = [
                    project3D(-w - 0.02, y - 0.12, d),
                    project3D(-w + 0.08, y - 0.12, d),
                    project3D(-w + 0.08, y + 0.12, d),
                    project3D(-w - 0.02, y + 0.12, d),
                  ];
                  return <polygon key={`lh-${t}`} points={pointsString(hinge)} fill="#1a1a1a" opacity="0.8" />;
                })}
                {/* Hinges - right door (right side) */}
                {[0.25, 0.5, 0.75].map(t => {
                  const y = -h + height * t;
                  const hinge = [
                    project3D(w - 0.08, y - 0.12, d),
                    project3D(w + 0.02, y - 0.12, d),
                    project3D(w + 0.02, y + 0.12, d),
                    project3D(w - 0.08, y + 0.12, d),
                  ];
                  return <polygon key={`rh-${t}`} points={pointsString(hinge)} fill="#1a1a1a" opacity="0.8" />;
                })}
              </>
            ) : face.isGlass ? (
              <>
                <polygon
                  points={pointsString(face.verts)}
                  fill="#88ccff"
                  stroke="#88ccff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  opacity="0.3"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#glassGradient)"
                  opacity="0.25"
                />
              </>
            ) : (
              <>
                <polygon
                  points={pointsString(face.verts)}
                  fill={`url(#${face.pattern})`}
                  stroke={darkerShade(stainHex, 15)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#woodSheen)"
                  opacity="0.5"
                />
              </>
            )}
            {/* Plinths: wooden strips at top (5%) and bottom (10%) of the front face */}
            {face.name === 'front' && (
              <>
                <polygon
                  points={pointsString([corners.fbl, corners.fbr, corners.bptr, corners.bptl])}
                  fill="url(#woodGrainBase)"
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                  data-testid="bottom-plinth"
                />
                <polygon
                  points={pointsString([corners.fbl, corners.fbr, corners.bptr, corners.bptl])}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
                <polygon
                  points={pointsString([corners.tpbl, corners.tpbr, corners.ftr, corners.ftl])}
                  fill="url(#woodGrainBase)"
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                  data-testid="top-plinth"
                />
                <polygon
                  points={pointsString([corners.tpbl, corners.tpbr, corners.ftr, corners.ftl])}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
              </>
            )}
          </g>
        ))}
      </g>
    );
  }, [width, height, depth, stainHex, useHingedDoors, project3D]);

  const renderCorner = useMemo(() => {
    const totalSize = width / 2;
    const h = height / 2;
    const armThickness = depth / 2;
    const ts = totalSize;
    const at = armThickness;
    
    const vertices = {
      A: project3D(-ts, -h, -ts),
      B: project3D(ts, -h, -ts),
      C: project3D(ts, -h, -ts + at),
      D: project3D(-ts + at, -h, -ts + at),
      E: project3D(-ts + at, -h, ts),
      F: project3D(-ts, -h, ts),
      A_t: project3D(-ts, h, -ts),
      B_t: project3D(ts, h, -ts),
      C_t: project3D(ts, h, -ts + at),
      D_t: project3D(-ts + at, h, -ts + at),
      E_t: project3D(-ts + at, h, ts),
      F_t: project3D(-ts, h, ts),
      // Plinth points for glassFront1 (runs along X axis, at z = -ts + at)
      f1_bptl: project3D(-ts + at, -h + bottomPlinthH, -ts + at),
      f1_bptr: project3D(ts, -h + bottomPlinthH, -ts + at),
      f1_tpbl: project3D(-ts + at, h - topPlinthH, -ts + at),
      f1_tpbr: project3D(ts, h - topPlinthH, -ts + at),
      // Plinth points for glassFront2 (runs along Z axis, at x = -ts + at)
      f2_bptl: project3D(-ts + at, -h + bottomPlinthH, ts),
      f2_bptr: project3D(-ts + at, -h + bottomPlinthH, -ts + at),
      f2_tpbl: project3D(-ts + at, h - topPlinthH, ts),
      f2_tpbr: project3D(-ts + at, h - topPlinthH, -ts + at),
    };
    
    // Hinge rendering helpers (3 hinges per door)
    const hingeOffsets = [0.25, 0.5, 0.75];
    const hingeSize = 0.12;
    // glassFront1 hinges on the LEFT side (x = -ts + at)
    const hingesF1 = hingeOffsets.map((t) => {
      const y = -h + height * t;
      return [
        project3D(-ts + at - 0.02, y - hingeSize, -ts + at),
        project3D(-ts + at + 0.08, y - hingeSize, -ts + at),
        project3D(-ts + at + 0.08, y + hingeSize, -ts + at),
        project3D(-ts + at - 0.02, y + hingeSize, -ts + at),
      ];
    });
    // glassFront2 hinges on the BACK side (z = -ts + at)
    const hingesF2 = hingeOffsets.map((t) => {
      const y = -h + height * t;
      return [
        project3D(-ts + at, y - hingeSize, -ts + at - 0.02),
        project3D(-ts + at, y - hingeSize, -ts + at + 0.08),
        project3D(-ts + at, y + hingeSize, -ts + at + 0.08),
        project3D(-ts + at, y + hingeSize, -ts + at - 0.02),
      ];
    });
    
    const faces = [
      { name: 'backWall1', verts: [vertices.A, vertices.B, vertices.B_t, vertices.A_t], isGlass: false, pattern: 'woodGrainWithKnots' },
      { name: 'backWall2', verts: [vertices.F, vertices.A, vertices.A_t, vertices.F_t], isGlass: false, pattern: 'woodGrainVertical' },
      { name: 'endCap1', verts: [vertices.B, vertices.C, vertices.C_t, vertices.B_t], isGlass: false, pattern: 'woodGrainVertical' },
      { name: 'endCap2', verts: [vertices.E, vertices.F, vertices.F_t, vertices.E_t], isGlass: false, pattern: 'woodGrainBase' },
      { name: 'glassFront1', verts: [vertices.D, vertices.C, vertices.C_t, vertices.D_t], isGlass: true, isHingedDoor: true, plinthBottom: [vertices.D, vertices.C, vertices.f1_bptr, vertices.f1_bptl], plinthTop: [vertices.f1_tpbl, vertices.f1_tpbr, vertices.C_t, vertices.D_t], hinges: hingesF1 },
      { name: 'glassFront2', verts: [vertices.E, vertices.D, vertices.D_t, vertices.E_t], isGlass: true, isHingedDoor: true, plinthBottom: [vertices.E, vertices.D, vertices.f2_bptr, vertices.f2_bptl], plinthTop: [vertices.f2_tpbl, vertices.f2_tpbr, vertices.D_t, vertices.E_t], hinges: hingesF2 },
      { name: 'top', verts: [vertices.A_t, vertices.B_t, vertices.C_t, vertices.D_t, vertices.E_t, vertices.F_t], isGlass: false, pattern: 'woodGrainBase' },
      { name: 'bottom', verts: [vertices.F, vertices.E, vertices.D, vertices.C, vertices.B, vertices.A], isGlass: false, pattern: 'woodGrainWithKnots' },
    ];
    
    const visibleFaces = faces.map(face => {
      const avgZ = face.verts.reduce((sum, v) => sum + v.z, 0) / face.verts.length;
      return { ...face, avgZ, visible: true };
    }).sort((a, b) => b.avgZ - a.avgZ);
    
    return (
      <g>
        {visibleFaces.map((face) => (
          <g key={face.name}>
            {face.isGlass ? (
              <>
                <polygon
                  points={pointsString(face.verts)}
                  fill="#88ccff"
                  stroke="#88ccff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  opacity="0.3"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#glassGradient)"
                  opacity="0.2"
                />
              </>
            ) : (
              <>
                <polygon
                  points={pointsString(face.verts)}
                  fill={`url(#${face.pattern})`}
                  stroke={darkerShade(stainHex, 15)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
              </>
            )}
            {/* Hinged-door extras: plinths (top 5%, bottom 10%) + hinges on one edge */}
            {face.isHingedDoor && (
              <>
                <polygon
                  points={pointsString(face.plinthBottom)}
                  fill="url(#woodGrainBase)"
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.plinthBottom)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
                <polygon
                  points={pointsString(face.plinthTop)}
                  fill="url(#woodGrainBase)"
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.plinthTop)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
                {face.hinges.map((hinge, idx) => (
                  <polygon
                    key={`${face.name}-hinge-${idx}`}
                    points={pointsString(hinge)}
                    fill="#1a1a1a"
                    opacity="0.85"
                  />
                ))}
              </>
            )}
          </g>
        ))}
      </g>
    );
  }, [width, height, depth, stainHex, project3D]);

  const renderHexagonal = useMemo(() => {
    const radius = width / 2.5;
    const h = height / 2;
    
    // Generate hexagon vertices
    const hexPoints = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6 - Math.PI / 2;
      hexPoints.push({
        x: Math.cos(angle) * radius,
        z: Math.sin(angle) * radius
      });
    }
    
    // Project all vertices
    const topVerts = hexPoints.map(p => project3D(p.x, h, p.z));
    const bottomVerts = hexPoints.map(p => project3D(p.x, -h, p.z));
    
    // Single hinged door on side 5 (the side most visible at the default camera angle)
    const doorSideIndex = 5;
    const doorNext = (doorSideIndex + 1) % 6;
    const p1 = hexPoints[doorSideIndex];
    const p2 = hexPoints[doorNext];
    // Plinth boundary verts for the door
    const doorBptl = project3D(p1.x, -h + bottomPlinthH, p1.z);
    const doorBptr = project3D(p2.x, -h + bottomPlinthH, p2.z);
    const doorTpbl = project3D(p1.x, h - topPlinthH, p1.z);
    const doorTpbr = project3D(p2.x, h - topPlinthH, p2.z);
    // Hinges on the p1 edge (3 hinges)
    const hingeSize = 0.12;
    // Compute a small outward normal for the hinge pucks (just push in +x of the edge slightly using mid-interp)
    const hinges = [0.25, 0.5, 0.75].map((t) => {
      const y = -h + height * t;
      // inset toward next vertex a little for hinge width
      const interp = (a, b, f) => a + (b - a) * f;
      const hx1 = interp(p1.x, p2.x, 0.0);
      const hz1 = interp(p1.z, p2.z, 0.0);
      const hx2 = interp(p1.x, p2.x, 0.08);
      const hz2 = interp(p1.z, p2.z, 0.08);
      return [
        project3D(hx1, y - hingeSize, hz1),
        project3D(hx2, y - hingeSize, hz2),
        project3D(hx2, y + hingeSize, hz2),
        project3D(hx1, y + hingeSize, hz1),
      ];
    });
    
    // Build faces
    const faces = [];
    
    // Side panels
    for (let i = 0; i < 6; i++) {
      const next = (i + 1) % 6;
      const isDoor = i === doorSideIndex;
      faces.push({
        name: `side${i}`,
        verts: [bottomVerts[i], bottomVerts[next], topVerts[next], topVerts[i]],
        isGlass: isDoor,
        isHingedDoor: isDoor,
        pattern: isDoor ? null : (i % 2 === 0 ? 'woodGrainVertical' : 'woodGrainWithKnots'),
        plinthBottom: isDoor ? [bottomVerts[i], bottomVerts[next], doorBptr, doorBptl] : null,
        plinthTop: isDoor ? [doorTpbl, doorTpbr, topVerts[next], topVerts[i]] : null,
        hinges: isDoor ? hinges : null,
      });
    }
    
    // Top and bottom
    faces.push({ name: 'top', verts: [...topVerts], isGlass: false, pattern: 'woodGrainBase' });
    faces.push({ name: 'bottom', verts: [...bottomVerts].reverse(), isGlass: false, pattern: 'woodGrainWithKnots' });
    
    const visibleFaces = faces.map(face => {
      const avgZ = face.verts.reduce((sum, v) => sum + v.z, 0) / face.verts.length;
      return { ...face, avgZ, visible: true };
    }).sort((a, b) => b.avgZ - a.avgZ);
    
    return (
      <g>
        {visibleFaces.map((face) => (
          <g key={face.name}>
            {face.isGlass ? (
              <>
                <polygon
                  points={pointsString(face.verts)}
                  fill="#88ccff"
                  stroke="#88ccff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  opacity="0.3"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#glassGradient)"
                  opacity="0.2"
                />
              </>
            ) : (
              <>
                <polygon
                  points={pointsString(face.verts)}
                  fill={`url(#${face.pattern})`}
                  stroke={darkerShade(stainHex, 15)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.verts)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
              </>
            )}
            {/* Plinths + hinges for the single hinged door */}
            {face.isHingedDoor && (
              <>
                <polygon
                  points={pointsString(face.plinthBottom)}
                  fill="url(#woodGrainBase)"
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.plinthBottom)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
                <polygon
                  points={pointsString(face.plinthTop)}
                  fill="url(#woodGrainBase)"
                  stroke={darkerShade(stainHex, 25)}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <polygon
                  points={pointsString(face.plinthTop)}
                  fill="url(#woodSheen)"
                  opacity="0.4"
                />
                {face.hinges.map((hinge, idx) => (
                  <polygon
                    key={`hex-hinge-${idx}`}
                    points={pointsString(hinge)}
                    fill="#1a1a1a"
                    opacity="0.85"
                  />
                ))}
              </>
            )}
          </g>
        ))}
      </g>
    );
  }, [width, height, stainHex, project3D]);

  return (
    <div 
      ref={containerRef}
      className="w-full h-full flex items-center justify-center bg-[#0A0A0A] relative select-none" 
      data-testid="enclosure-viewer"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-full max-w-[600px] max-h-[400px]"
        style={{ filter: 'drop-shadow(0 0 20px rgba(224, 122, 95, 0.2))' }}
      >
        <defs>
          {/* Wood grain patterns - dynamically colored based on selected stain */}
          <WoodGrainDefs stainHex={stainHex} />
          
          <linearGradient id="glassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#88ccff" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.2" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Background grid */}
        <g opacity="0.1">
          {[...Array(10)].map((_, i) => (
            <line
              key={`h${i}`}
              x1="0"
              y1={i * 30 + 15}
              x2={svgWidth}
              y2={i * 30 + 15}
              stroke="#E07A5F"
              strokeWidth="0.5"
            />
          ))}
          {[...Array(14)].map((_, i) => (
            <line
              key={`v${i}`}
              x1={i * 30 + 10}
              y1="0"
              x2={i * 30 + 10}
              y2={svgHeight}
              stroke="#E07A5F"
              strokeWidth="0.5"
            />
          ))}
        </g>
        
        {/* Enclosure based on shape */}
        <g filter="url(#glow)">
          {shape === "rectangular" && renderRectangular}
          {shape === "corner" && renderCorner}
          {shape === "hexagonal" && renderHexagonal}
        </g>
        
        {/* Dimension labels */}
        <g fill="#F4F1DE" fontSize="12" fontFamily="Space Grotesk, sans-serif">
          <text x={svgWidth / 2} y={svgHeight - 10} textAnchor="middle" opacity="0.6">
            {width}ft W × {height}ft H × {depth}ft D
          </text>
        </g>
      </svg>
      
      {/* Rotation controls */}
      <div className="absolute bottom-4 left-4 flex items-center gap-3">
        <div className="flex items-center gap-2 text-[#F4F1DE]/50 text-xs bg-[#121212]/80 backdrop-blur px-3 py-2 rounded border border-white/10">
          <Move className="w-3 h-3" />
          <span>Drag to rotate</span>
        </div>
        <button
          onClick={resetRotation}
          className="flex items-center gap-1.5 text-[#F4F1DE]/60 hover:text-[#E07A5F] text-xs bg-[#121212]/80 backdrop-blur px-3 py-2 rounded border border-white/10 hover:border-[#E07A5F]/50 transition-colors"
          title="Reset rotation"
        >
          <RotateCcw className="w-3 h-3" />
          <span>Reset</span>
        </button>
      </div>
      
      {/* Rotation indicator */}
      <div className="absolute top-4 right-4 text-[#F4F1DE]/30 text-xs bg-[#121212]/60 backdrop-blur px-2 py-1 rounded">
        {Math.round(rotationY)}° / {Math.round(rotationX)}°
      </div>
    </div>
  );
};

export default EnclosureViewer;
